import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import STXReferenceNumber from "@/components/common/STXReferenceNumber";
import { Loader2, PackageSearch } from "lucide-react";

const STATUS_TINT = {
  REQUESTED: "bg-slate-100 text-slate-600 border-slate-200",
  ELIGIBILITY_REVIEW: "bg-amber-50 text-amber-700 border-amber-200",
  APPROVED: "bg-emerald-50 text-emerald-700 border-emerald-200",
  COLLECTION_PENDING: "bg-sky-50 text-sky-700 border-sky-200",
  RECEIVED: "bg-indigo-50 text-indigo-700 border-indigo-200",
  INSPECTION: "bg-violet-50 text-violet-700 border-violet-200",
  OEM_RMA: "bg-orange-50 text-orange-700 border-orange-200",
  REPAIR: "bg-blue-50 text-blue-700 border-blue-200",
  REPLACEMENT: "bg-cyan-50 text-cyan-700 border-cyan-200",
  DISPATCH: "bg-teal-50 text-teal-700 border-teal-200",
  DELIVERED: "bg-emerald-50 text-emerald-700 border-emerald-200",
  RECOVERY_PENDING: "bg-slate-100 text-slate-600 border-slate-200",
  CLOSED: "bg-slate-100 text-slate-500 border-slate-200",
  REJECTED: "bg-rose-100 text-rose-700 border-rose-200",
};
const STAGE_LABEL = {
  REQUESTED: "Request received", ELIGIBILITY_REVIEW: "Under review", APPROVED: "Approved",
  COLLECTION_PENDING: "Pickup arranged", RECEIVED: "Device received", INSPECTION: "Being inspected",
  OEM_RMA: "With manufacturer", REPAIR: "Under repair", REPLACEMENT: "Replacement in progress",
  DISPATCH: "Dispatched", DELIVERED: "Delivered", RECOVERY_PENDING: "Finalising", CLOSED: "Closed",
  REJECTED: "Not approved",
};
const fmt = (d) => (d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—");

export default function OurRma() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/rma/customer/cases").then((r) => setItems(r.data.items || [])).catch(() => setItems([])); }, []);

  return (
    <div className="max-w-4xl mx-auto p-5 space-y-5" data-testid="our-rma-page">
      <div>
        <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-slate-500">Services</div>
        <h1 className="text-3xl font-semibold text-slate-900 tracking-tight mt-1">Our RMA</h1>
        <p className="text-sm text-slate-600 mt-1">Track your returns, repairs and replacements — live status and progress.</p>
      </div>
      {items === null ? (
        <div className="py-20 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
      ) : items.length === 0 ? (
        <div className="py-16 text-center" data-testid="our-rma-empty">
          <PackageSearch className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <div className="text-sm text-slate-500">You have no RMA requests yet.</div>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((c) => (
            <div key={c.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`our-rma-${c.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-900"><STXReferenceNumber value={c.rma_no} copy size={12} /></div>
                  <div className="text-[13px] text-slate-600 truncate">{c.product || "—"}{c.serial ? ` · ${c.serial}` : ""}</div>
                  <div className="text-[11px] text-slate-400 mt-0.5">Raised {fmt(c.created_at)}</div>
                </div>
                <Badge className={"text-[10px] border shrink-0 " + (STATUS_TINT[c.status] || STATUS_TINT.REQUESTED)}>{STAGE_LABEL[c.status] || c.status}</Badge>
              </div>
              {c.collection?.awb && (
                <div className="mt-2 text-[12px] text-slate-500">Shipment: {c.collection.courier || "Courier"} · AWB {c.collection.awb}</div>
              )}
              {c.resolution_summary && (
                <div className="mt-1 text-[12px] text-emerald-700">Resolution: {c.resolution_summary.replace(/_/g, " ")}</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
